import { Link } from "react-router-dom";
import { Heart, Users, Award, ShieldCheck, CheckCircle2, Stethoscope, Building2, ArrowUpRight, Sparkles } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import AcceptedInsurance from "@/components/AcceptedInsurance";
import { Button } from "@/components/ui/button";
import { SITE } from "@/lib/site";

const VALUES = [
  { icon: Heart, title: "Compassionate", body: "A safe, judgment-free space." },
  { icon: Users, title: "Collaborative", body: "Care planned with you, not for you." },
  { icon: Award, title: "Experienced", body: "10+ years across all ages." },
  { icon: ShieldCheck, title: "Personalized", body: "Plans tailored to your goals." },
];

export default function About() {
  return (
    <div className="min-h-screen">
      <Navigation />

      {/* HERO */}
      <section className="relative pt-28 md:pt-32 pb-10 md:pb-14">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="glow-orb -top-10 -left-32 w-[420px] h-[420px] bg-primary/30" />
        <div className="container mx-auto relative z-10 text-center max-w-4xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/5 text-primary text-xs font-medium mb-6">
            <Heart className="w-3.5 h-3.5" /> Compassionate Psychiatric Care
          </div>
          <h1 data-testid="about-title" className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-balance">
            Care that <em className="not-italic gradient-text font-light">listens.</em>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-xl mx-auto text-pretty">
            Compassionate psychiatric care for children, teens, and adults across {SITE.state}.
          </p>
        </div>
      </section>

      <main className="pb-10 md:pb-14">
        <div className="container mx-auto">
          <div className="max-w-6xl mx-auto">

            {/* MISSION */}
            <section data-testid="about-mission" className="mb-14 md:mb-8 md:mb-12">
              <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-primary via-[hsl(178_72%_30%)] to-[hsl(155_55%_38%)] text-white p-6 md:p-12 text-center">
                <div className="glow-orb -top-32 -left-20 w-[420px] h-[420px] bg-white/15 opacity-50" />
                <div className="glow-orb -bottom-32 -right-10 w-[420px] h-[420px] bg-accent/40 opacity-50" />
                <div className="relative z-10 max-w-3xl mx-auto">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/25 text-xs font-medium mb-6">
                    <Sparkles className="w-3.5 h-3.5" /> Our Mission
                  </div>
                  <p className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight text-balance">
                    To create a <em className="not-italic">safe and supportive space</em> where you and your family feel
                    {" "}<strong className="font-semibold">heard, understood, and empowered</strong>{" "}
                    to achieve lasting mental wellness.
                  </p>
                </div>
              </div>
            </section>

            {/* About the practice */}
            <section className="mb-14 md:mb-8 md:mb-12">
              <div className="flex items-center gap-3 mb-8 justify-center">
                <span className="grid place-items-center w-11 h-11 rounded-2xl bg-primary/10 border border-primary/20 text-primary">
                  <Building2 className="w-5 h-5" />
                </span>
                <h2 className="font-display text-3xl md:text-4xl tracking-tight">About {SITE.short}</h2>
              </div>

              <div className="rounded-3xl border border-border bg-card/40 p-6 md:p-10 space-y-5">
                <p className="text-lg leading-relaxed">
                  A trusted psychiatric practice with <strong className="text-primary">10+ years of experience</strong> helping children, teens, and adults — ages 6 and above — manage ADHD, anxiety, depression, DMDD, and bipolar disorder.
                </p>

                <blockquote className="relative rounded-2xl border-l-2 border-primary pl-6 py-2 italic text-lg leading-relaxed">
                  &ldquo;Care that feels safe, supportive, and judgment-free.&rdquo;
                </blockquote>
              </div>

              {/* Values grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
                {VALUES.map((v) => (
                  <div
                    key={v.title}
                    data-testid={`about-value-${v.title.toLowerCase()}`}
                    className="rounded-2xl border border-border bg-card/40 p-6 transition-all hover:border-primary/40"
                  >
                    <span className="inline-grid place-items-center w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 text-primary mb-4">
                      <v.icon className="w-5 h-5" />
                    </span>
                    <h3 className="font-display text-xl tracking-tight mb-2">{v.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{v.body}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Provider */}
            <section className="mb-14 md:mb-8 md:mb-12">
              <div className="flex items-center gap-3 mb-8 justify-center">
                <span className="grid place-items-center w-11 h-11 rounded-2xl bg-accent/10 border border-accent/20 text-accent">
                  <Stethoscope className="w-5 h-5" />
                </span>
                <h2 className="font-display text-3xl md:text-4xl tracking-tight">Meet your provider</h2>
              </div>

              <div className="rounded-3xl border border-border bg-card/40 p-6 md:p-10">
                <div className="grid md:grid-cols-12 gap-10 items-start">
                  <div className="md:col-span-4 mx-auto md:mx-0">
                    <div className="relative rounded-2xl overflow-hidden border border-border ring-glow w-full max-w-[280px]">
                      <img
                        src="/images/provider-portrait.jpg"
                        alt={`${SITE.provider.name}, ${SITE.provider.credentials}`}
                        className="w-full aspect-[4/5] object-cover"
                      />
                    </div>
                    <div className="mt-5 text-center md:text-left">
                      <h3 className="font-display text-2xl tracking-tight">{SITE.provider.name}</h3>
                      <p className="text-primary font-semibold">{SITE.provider.credentials}</p>
                      <p className="text-sm text-muted-foreground mt-1">{SITE.provider.title}</p>
                    </div>
                  </div>
                  <div className="md:col-span-8 space-y-5">
                    <p className="text-lg leading-relaxed">
                      <strong className="text-primary">Board-Certified Psychiatric Mental Health Nurse Practitioner</strong> with Full Practice Authority — specializing in children, teens, and adults across {SITE.state}.
                    </p>
                    <p className="text-lg leading-relaxed text-muted-foreground">
                      Areas of focus: ADHD, anxiety, depression, DMDD, bipolar, and other mood-related challenges. Secure telehealth visits available statewide.
                    </p>

                    <div className="rounded-2xl border-l-2 border-primary pl-5 py-2 mt-4">
                      <p className="italic">
                        &ldquo;To help every patient feel more confident, regulated, and successful — and to support their families, too.&rdquo;
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* What to expect */}
            <section className="mb-14 md:mb-8 md:mb-12 rounded-3xl border border-border bg-card/40 p-6 md:p-10">
              <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-6">What to expect</h2>
              <p className="text-lg leading-relaxed mb-8 text-muted-foreground max-w-2xl">
                A calm, welcoming space — no pressure, no judgment. Your first visit will include:
              </p>

              <ul className="grid md:grid-cols-2 gap-3">
                {[
                  "Comprehensive symptom assessment",
                  "Open discussion of your goals",
                  "A personalized care plan",
                  "Education on diagnosis & options",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 rounded-xl border border-border bg-background/40 px-5 py-3.5">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Insurance */}
            <section className="mb-8 md:mb-12 rounded-3xl border border-border bg-card/40 p-6 md:p-10">
              <AcceptedInsurance />
            </section>

            {/* CTA */}
            <section className="text-center rounded-3xl border border-border bg-card/40 p-6 md:p-10">
              <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-3">
                Ready when you are.
              </h2>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
                <Button
                  asChild
                  size="lg"
                  data-testid="about-cta-portal"
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-12 px-7"
                >
                  <Link to="/contact">
                    Start here
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full border-border h-12 px-7">
                  <Link to="/contact">Contact us</Link>
                </Button>
              </div>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
